<template>
  <m-content class="app-feedback">
    <m-btn
      fab
      icon="close"
      xs
      light
      hide-border
      class="_close"
      @click="dismiss"
    />
    <div
      class="_body"
      :style="bodyStyle"
    >
      <div
        ref="content"
        class="_content"
      >
        <transition
          name="app-feedback-fade"
          mode="out-in"
        >
          <div
            v-if="!submitted"
            key="ask"
          >
            <p class="_question">
              {{ $t('appFeedback.npsQuestion') }}
            </p>
            <p class="_muted _intro">
              {{ $t('appFeedback.npsIntro') }}
            </p>
            <div
              class="_scale"
              role="radiogroup"
              :aria-label="$t('appFeedback.npsQuestion')"
            >
              <button
                v-for="(_, value) in 11"
                :key="value"
                type="button"
                role="radio"
                :aria-checked="selectedValue === value"
                :class="['_score', { '-selected': selectedValue === value }]"
                @click="recordScore(value)"
              >
                {{ value }}
              </button>
            </div>
            <div class="_muted _scale-labels">
              <span>{{ $t('appFeedback.npsScale.left') }}</span>
              <span>{{ $t('appFeedback.npsScale.right') }}</span>
            </div>
            <transition
              name="app-feedback-fade"
              @after-enter="commentField?.focus({ preventScroll: true })"
            >
              <div
                v-if="scoreBand !== null"
                class="_comment"
              >
                <p class="_follow-up">
                  {{ $t(`appFeedback.followUp.${scoreBand}`) }}
                </p>
                <m-textarea
                  ref="commentField"
                  v-model:value="comment"
                  :rows="2"
                  full-width
                  :placeholder="$t('appFeedback.hint')"
                />
                <div class="_actions">
                  <m-btn
                    color="primary"
                    small
                    @click="submit"
                  >
                    {{ $t('general.send') }}
                  </m-btn>
                </div>
              </div>
            </transition>
            <div
              v-if="scoreBand === null"
              class="_footer"
            >
              <m-btn
                xs
                light
                hide-border
                @click="optOut"
              >
                {{ $t('appFeedback.dontAskAgain') }}
              </m-btn>
            </div>
          </div>
          <div
            v-else
            key="thanks"
            class="_thanks"
          >
            <span class="_check">
              <m-icon type="check" />
            </span>
            <p class="_thanks-text">
              {{ $t(`appFeedback.thankYou.${scoreBand}`) }}
            </p>
            <m-btn
              v-if="scoreBand === promoter"
              :href="referral.link"
              target="_blank"
              small
              @click="openReferral"
            >
              {{ $t('appFeedback.referral', { name: referral.name }) }}
              <m-icon
                type="external-link"
                :style="{ marginLeft: '.6rem' }"
              />
            </m-btn>
            <i18n-t
              keypath="appFeedback.anytimeHint"
              tag="p"
              class="_muted"
            >
              <template #icon>
                <m-icon
                  type="question-circle"
                  class="_hint-icon"
                />
              </template>
            </i18n-t>
          </div>
        </transition>
      </div>
    </div>
  </m-content>
</template>

<script setup>
import useAppFeedback from '@/composables/app-feedback/app-feedback';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import { appFeedbackType } from 'shared/constants.json';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { isNullOrUndefined } from 'shared/lib/object/object';

const emit = defineEmits(['close']);

const { loggedInUser } = useLoggedInUser();
const { loggedInUserAccount } = useLoggedInUserAccount();
const { mutateAppFeedback } = useAppFeedback();

const referral = { name: 'G2', link: 'https://www.g2.com/products/mooncamp/reviews', key: 'g2' };
const thankYouDurationMs = 4000;
const detractor = 'detractor';
const passive = 'passive';
const promoter = 'promoter';

const selectedValue = ref(null);
const comment = ref('');
const submitted = ref(false);
let closeTimer;

const content = ref(null);
// Focusing without preventScroll would scroll the clipped, still-growing body and make the reveal jump.
const commentField = ref(null);
const contentHeight = ref(null);
const bodyStyle = computed(() => (contentHeight.value === null ? {} : { height: `${contentHeight.value}px` }));
const contentObserver = new ResizeObserver(([entry]) => {
  if (entry.target.offsetHeight > 0) {
    contentHeight.value = entry.target.offsetHeight;
  }
});
onMounted(() => contentObserver.observe(content.value));

const scoreBand = computed(() => {
  if (selectedValue.value === null) {
    return null;
  }
  if (selectedValue.value <= 6) {
    return detractor;
  }
  if (selectedValue.value <= 8) {
    return passive;
  }
  return promoter;
});

const newEntry = () => ({
  user: { uid: loggedInUser.value.uid },
  account: { uid: loggedInUserAccount.value.uid },
  value: selectedValue.value,
  comment: comment.value,
  referralLinkClicked: false,
  referralTarget: referral.key,
  ignored: selectedValue.value === null,
  optedOut: false,
  type: appFeedbackType.nps,
});

// Stored on the first score click and amended afterwards, so a closed tab never loses
// a given score; writes are chained to stay ordered and reuse the created uid.
let savedEntry = Promise.resolve(null);
const persist = (changes) => {
  savedEntry = savedEntry.then((saved) => {
    const payload = isNullOrUndefined(saved) ? { ...newEntry(), ...changes } : { uid: saved.uid, ...changes };
    return mutateAppFeedback(payload).catch(() => saved);
  });
};

const close = () => {
  clearTimeout(closeTimer);
  emit('close');
};

const recordScore = (value) => {
  selectedValue.value = value;
  persist({ value });
};

const dismiss = () => {
  if (selectedValue.value === null) {
    persist({});
  } else if (!submitted.value && comment.value !== '') {
    persist({ comment: comment.value });
  }
  close();
};

const optOut = () => {
  persist({ optedOut: true });
  close();
};

const openReferral = () => {
  persist({ referralLinkClicked: true });
  close();
};

const submit = () => {
  persist({ comment: comment.value });
  submitted.value = true;
  if (scoreBand.value !== promoter) {
    closeTimer = setTimeout(close, thankYouDurationMs);
  }
};

onBeforeUnmount(() => {
  clearTimeout(closeTimer);
  contentObserver.disconnect();
});
</script>

<style scoped lang="scss" type="text/scss">
  .app-feedback {
    position: relative;
    padding: 2rem 2.4rem;

    p {
      margin: 0;
    }

    ._close {
      position: absolute;
      top: 1.2rem;
      right: 1.2rem;
    }

    ._muted {
      font-size: $font-size-2;
      line-height: 1.5;
      color: $font-color-secondary;
    }

    ._question {
      padding-right: 2.8rem;
      font-size: $font-size-4;
      font-weight: $font-weight-medium;
      line-height: 1.45;
    }

    ._intro {
      margin-top: .4rem;
    }

    ._scale {
      display: flex;
      margin-top: 1.6rem;
      overflow: hidden;
      border: 1px solid $border-color-dark;
      border-radius: .6rem;
    }

    ._score {
      flex: 1;
      height: 3.2rem;
      padding: 0;
      font: inherit;
      font-size: $font-size-3;
      font-weight: $font-weight-medium;
      color: $font-color-primary;
      cursor: pointer;
      background: none;
      border: none;
      border-left: 1px solid $border-color-dark;
      transition: background-color .15s ease, color .15s ease;

      &:first-child {
        border-left: none;
      }

      &:hover {
        background-color: $hover-color;
      }

      &.-selected {
        color: $white;
        background-color: $primary-color;
      }

      &:focus-visible {
        outline: 2px solid $primary-color;
        outline-offset: -2px;
      }
    }

    ._scale-labels {
      display: flex;
      justify-content: space-between;
      margin-top: .6rem;
    }

    ._body {
      overflow: hidden;
      transition: height .3s cubic-bezier(.2, .8, .2, 1);
    }

    ._content {
      display: flow-root;
    }

    .app-feedback-fade-enter-active,
    .app-feedback-fade-leave-active {
      transition: opacity .2s ease;
    }

    .app-feedback-fade-enter-from,
    .app-feedback-fade-leave-to {
      opacity: 0;
    }

    ._follow-up {
      margin: 1.6rem 0 .8rem;
      font-size: $font-size-3;
      font-weight: $font-weight-medium;
    }

    ._actions {
      display: flex;
      justify-content: flex-end;
      margin-top: .8rem;
    }

    ._footer {
      display: flex;
      justify-content: flex-end;
      margin-top: 1.2rem;
    }

    ._thanks {
      display: flex;
      flex-direction: column;
      gap: .8rem;
      align-items: center;
      padding: .8rem 1.6rem;
      text-align: center;
    }

    ._check {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 4rem;
      height: 4rem;
      margin-bottom: .4rem;
      line-height: 0;
      color: $white;
      background-color: $success-color;
      border-radius: 50%;
      animation: pop .4s cubic-bezier(.34, 1.56, .64, 1);
    }

    ._hint-icon {
      display: inline-flex;
      vertical-align: -.15em;
    }

    ._thanks-text {
      font-size: $font-size-4;
      font-weight: $font-weight-medium;
    }

    @keyframes pop {
      0% {
        opacity: 0;
        transform: scale(.4);
      }

      100% {
        opacity: 1;
        transform: scale(1);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      ._body,
      ._score,
      .app-feedback-fade-enter-active,
      .app-feedback-fade-leave-active {
        transition: none;
      }

      ._check {
        animation: none;
      }
    }
  }
</style>
